import { useState } from "react";
import { FlowPrimaryButton } from "@/components/consultation-flow/ui";
import type { DateInputNode as DateInputNodeType } from "@/lib/pharmacyFirst/types";

export interface DateInputAnswer {
  iso: string;
  approximate: boolean;
}

interface DateInputNodeProps {
  node: DateInputNodeType;
  isDarkMode?: boolean;
  stepNumber?: number;
  onAnswer: (answer: DateInputAnswer) => void;
}

function withinBounds(iso: string, node: DateInputNodeType): boolean {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return false;
  const today = new Date();
  const diffDays = (date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);
  if (node.min !== undefined && diffDays < node.min) return false;
  if (node.max !== undefined && diffDays > node.max) return false;
  return true;
}

export function DateInputNode({
  node,
  isDarkMode = false,
  stepNumber,
  onAnswer,
}: DateInputNodeProps) {
  const [mode, setMode] = useState<"exact" | "approximate">("exact");
  const [exactDate, setExactDate] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");

  const approximateIso =
    node.allowApproximate && month && year
      ? `${year}-${month.padStart(2, "0")}-01`
      : null;

  const candidateIso = mode === "exact" ? exactDate : approximateIso;
  const valid = candidateIso !== null && candidateIso !== "" && withinBounds(candidateIso, node);

  const handleContinue = () => {
    if (!candidateIso || !valid) return;
    onAnswer({ iso: new Date(candidateIso).toISOString(), approximate: mode === "approximate" });
  };

  const cardClass = `border rounded-2xl p-4 ${
    isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
  }`;

  const inputClass = `w-full rounded-lg border-2 px-3 py-2.5 text-sm font-medium focus:outline-none ${
    isDarkMode
      ? "bg-gray-700 text-white border-gray-600"
      : "bg-white text-gray-900 border-gray-300"
  }`;

  return (
    <div className={cardClass}>
      <div className="flex items-center gap-3 mb-3">
        {stepNumber !== undefined && (
          <div
            className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold flex-shrink-0 ${
              isDarkMode ? "bg-white text-black" : "bg-black text-white"
            }`}
          >
            {stepNumber}
          </div>
        )}
        <div>
          <h3 className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
            {node.text}
          </h3>
          {node.hint && (
            <p className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>{node.hint}</p>
          )}
        </div>
      </div>

      {node.allowApproximate && (
        <div className="flex gap-2 mb-3">
          <button
            type="button"
            onClick={() => setMode("exact")}
            className={`text-xs px-3 py-1.5 rounded-md border ${
              mode === "exact"
                ? "border-blue-500 bg-blue-50 text-blue-800"
                : isDarkMode
                ? "border-gray-600 text-gray-300"
                : "border-gray-200 text-gray-600"
            }`}
          >
            Exact date
          </button>
          <button
            type="button"
            onClick={() => setMode("approximate")}
            className={`text-xs px-3 py-1.5 rounded-md border ${
              mode === "approximate"
                ? "border-blue-500 bg-blue-50 text-blue-800"
                : isDarkMode
                ? "border-gray-600 text-gray-300"
                : "border-gray-200 text-gray-600"
            }`}
          >
            Approximate (month/year)
          </button>
        </div>
      )}

      {mode === "exact" ? (
        <input
          type="date"
          value={exactDate}
          onChange={(e) => setExactDate(e.target.value)}
          className={inputClass}
          aria-label={node.text}
        />
      ) : (
        <div className="flex gap-2">
          <input
            type="number"
            placeholder="MM"
            min={1}
            max={12}
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className={inputClass}
            aria-label="Month"
          />
          <input
            type="number"
            placeholder="YYYY"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            className={inputClass}
            aria-label="Year"
          />
        </div>
      )}

      <FlowPrimaryButton
        label="Continue"
        className="w-full mt-4"
        onClick={handleContinue}
        disabled={!valid}
        blockedReason="Enter a valid date to continue."
      />
    </div>
  );
}
